import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "../LoadingOverlay";
import ConfirmDialog from "../ConfirmDialog";
import { useSelector } from "react-redux";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";
const rdOnly =
  "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-500 cursor-not-allowed";

function MaterialGroupModal({ dataSet, onClose, refreshData }) {
  const [groupId, setGroupId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupCode, setGroupCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [usage, setUsage] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.user_lvl === 1;

  useEffect(() => {
    if (dataSet) {
      setGroupId(dataSet.group_id);
      setGroupName(dataSet.group_name);
      setGroupCode(dataSet.group_code);
    }
  }, [dataSet]);

  const updateGroup = async () => {
    setIsLoading(true);
    try {
      const res = await axios.patch(
        "https://eps.elkomprintama.com/patch/api/store/store/material_group",
        {
          group_name: groupName.toLocaleUpperCase(),
          group_code: groupCode.toLocaleUpperCase(),
          group_id: groupId,
        },
      );
      if (res.status === 200) {
        toast.success(res.data.msg);
        onClose();
        refreshData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Update failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const openDelete = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/group/${groupId}/usage`,
      );
      setUsage(res.data.result);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to check group usage.");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteGroup = async () => {
    setIsLoading(true);
    try {
      const res = await axios.delete(
        `https://eps.elkomprintama.com/delete/api/store/group/${groupId}`,
      );
      toast.success(res.data.msg);
      setUsage(null);
      onClose();
      refreshData();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Delete failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!groupName) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
      {isLoading && <LoadingOverlay message="Updating…" />}

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-bold text-gray-800">
            Edit Material Group
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">ID: {groupId}</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <div>
          <label className={lbl}>Group ID</label>
          <input
            className={rdOnly}
            name="groupid"
            type="text"
            value={groupId}
            disabled
            readOnly
          />
        </div>

        <div>
          <label className={lbl}>Group Name</label>
          <input
            className={inp}
            name="groupname"
            type="text"
            placeholder="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </div>

        <div>
          <label className={lbl}>Group Code</label>
          <input
            className={inp}
            name="groupcode"
            type="text"
            placeholder="Group Code"
            value={groupCode}
            onChange={(e) => setGroupCode(e.target.value)}
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Update
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
          >
            Close
          </button>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={openDelete}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 transition"
          >
            Delete Group
          </button>
        )}
      </form>

      {usage && (
        <div className="absolute inset-0 bg-white flex flex-col px-6 py-5 space-y-4 z-10">
          <div>
            <h2 className="text-sm font-bold text-red-600">
              Delete Material Group
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {usage.group.group_name}
              {usage.group.group_code && ` · ${usage.group.group_code}`}
            </p>
          </div>

          {usage.materials.length > 0 ? (
            <>
              <p className="text-xs text-red-600">
                This group still has {usage.materials.length} material(s). Move
                them to another group or delete them first.
              </p>
              <ul className="flex-1 min-h-0 overflow-auto text-xs text-gray-700 space-y-0.5 border border-gray-200 rounded-lg px-4 py-3">
                {usage.materials.map((m) => (
                  <li key={m.product_id}>
                    <b>{m.product_code}</b> · {m.product_name} ·{" "}
                    {m.transactions} transaction(s)
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-xs text-red-600">
              This group has no materials. Deleting it cannot be undone.
            </p>
          )}

          <div className="flex gap-2 pt-1 mt-auto">
            {usage.materials.length === 0 && (
              <button
                type="button"
                onClick={deleteGroup}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition"
              >
                Delete Permanently
              </button>
            )}
            <button
              type="button"
              onClick={() => setUsage(null)}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Update this material group?"
          onConfirm={() => {
            setShowConfirm(false);
            updateGroup();
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
}

export default MaterialGroupModal;
